<template>
  <metainfo>
    <template #title="{ content }"
      >{{ content ? `${content} | DevStride` : 'DevStride' }}
    </template>
  </metainfo>
  <div v-show="!loadingOrganizations" :style="cssVars" class="tw-relative">
    <router-view />
  </div>
  <!-- Singleton dialog for programmatic $dialog() calls -->
  <confirm-dialog />
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, watch } from 'vue';
import { useMeta } from 'vue-meta';
import { useRoute } from 'vue-router';
import { organizationData } from './store/helpers';
import { useThemedColors } from './utils/color';
import { Colors } from '@/api';
import { toggleGlobalLoader } from '@/utils/helpers';
import ConfirmDialog from '@/components/modal/dialogs/ConfirmDialog.vue';

useMeta({
  title: '',
  htmlAttrs: { lang: 'en', amp: true },
});
const { loadingOrganizations } = organizationData();
const { appColors } = useThemedColors();
const route = useRoute();

// Check if we're on a page that doesn't load organizations
const isPublicPage = computed(() => {
  const publicPaths = [
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
  ];
  return publicPaths.some((path) => route.path.startsWith(path));
});

// Apply theme immediately before mounting
const applyStoredTheme = () => {
  // Public form pages use OS preference for light/dark only (ignore app themes)
  if (
    route.path.startsWith('/item-request/') ||
    route.path.startsWith('/stored-view/')
  ) {
    const prefersDark =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute(
      'data-theme',
      prefersDark ? 'dark' : 'light'
    );
    return;
  }

  const storedTheme = localStorage.getItem('ds:enabled-theme');
  if (storedTheme) {
    // Remove quotes from stored value
    const theme = storedTheme.replace(/"/g, '');
    document.documentElement.setAttribute('data-theme', theme);
  } else {
    // Default to light theme
    document.documentElement.setAttribute('data-theme', 'light');
  }
};

// Apply theme before component mounts
applyStoredTheme();

// Watch for route changes to handle theme switching
watch(
  () => route.path,
  () => {
    applyStoredTheme();
  }
);

// Hide the HTML global loader when appropriate
onMounted(() => {
  // For public pages, let the page component handle hiding the loader
  if (!isPublicPage.value) {
    // On protected pages, wait for organizations to load AND components to render
    watch(loadingOrganizations, (isLoading) => {
      if (!isLoading) {
        // Add a delay to ensure Vue components are rendered
        nextTick(() => {
          setTimeout(() => {
            toggleGlobalLoader(false);
          }, 100);
        });
      }
    });
  }
});

const cssVars = computed(() => {
  const colorVariables: Record<string, string> = {};
  for (const color of Object.values(Colors)) {
    colorVariables[`--color-${color}`] = appColors.value[color];
  }
  return colorVariables;
});
</script>

<style lang="scss"></style>
